/**
 * Duplicar em outra escola — destaque do nome da escola no nome do clone.
 *
 * Quando um recurso é duplicado para OUTRA escola, o título costuma carregar o
 * nome da escola de origem ("Turma PP Universo 2026"). Este helper acha essas
 * ocorrências (comparação sem acento/caixa, palavra por palavra) para o modal
 * destacar o trecho e o usuário ajustar antes de duplicar.
 *
 * Global: window.lbCrossOrgNome — usado pelos três modais de duplicação
 * (lista, single genérico e cabeçalho de recurso), que são templates PHP
 * independentes.
 *
 * @package Labead
 */

const STOPWORDS = [ 'de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'com', 'a', 'o', 'as', 'os', 'escola', 'curso', 'cursos', 'ead', 'ltda', 'me', 'sa', 'eireli' ];

/**
 * Normaliza um caractere: minúsculo e sem acento, preservando o mapeamento
 * 1:1 de índices com a string original (por isso não usa replace global).
 */
function normChar(ch) {
	const n = ch.normalize('NFD');
	return (n[0] || ch).toLowerCase();
}

function norm(str) {
	let out = '';
	for (const ch of String(str || '')) out += normChar(ch);
	return out;
}

function escapeHtml(str) {
	return String(str || '')
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/**
 * Termos de busca a partir do nome da escola: o nome inteiro + cada palavra
 * significativa (3+ letras, fora das stopwords), maiores primeiro.
 */
function termos(escolaNome) {
	const nome = norm(escolaNome).trim();
	if (!nome) return [];
	const lista = [ nome ];
	nome.split(/[^a-z0-9]+/).forEach((p) => {
		if (p.length >= 3 && !STOPWORDS.includes(p)) lista.push(p);
	});
	return [ ...new Set(lista) ].sort((a, b) => b.length - a.length);
}

/**
 * Faixas [ini, fim) do nome que casam com o nome (ou parte do nome) da escola.
 */
function faixas(nome, escolaNome) {
	const alvo = norm(nome);
	const found = [];
	termos(escolaNome).forEach((t) => {
		let idx = alvo.indexOf(t);
		while (idx !== -1) {
			// Só palavra inteira: vizinho alfanumérico invalida o match.
			const antes = idx > 0 ? alvo[idx - 1] : '';
			const depois = alvo[idx + t.length] || '';
			if (!/[a-z0-9]/.test(antes) && !/[a-z0-9]/.test(depois)) {
				found.push([ idx, idx + t.length ]);
			}
			idx = alvo.indexOf(t, idx + 1);
		}
	});
	if (!found.length) return [];
	// Une faixas sobrepostas E faixas vizinhas separadas só por pontuação/espaço
	// ("Aero" + "364" vira uma faixa "Aero 364" — senão a substituição pela
	// escola de destino duplicaria o nome novo).
	found.sort((a, b) => a[0] - b[0]);
	const merged = [ found[0].slice() ];
	for (let i = 1; i < found.length; i++) {
		const last = merged[merged.length - 1];
		const gap = alvo.slice(last[1], found[i][0]);
		if (found[i][0] <= last[1] || /^[^a-z0-9]*$/.test(gap)) {
			last[1] = Math.max(last[1], found[i][1]);
		} else {
			merged.push(found[i].slice());
		}
	}
	return merged;
}

window.lbCrossOrgNome = {
	/**
	 * true quando o nome contém o nome (ou parte do nome) da escola.
	 */
	contem(nome, escolaNome) {
		return faixas(nome, escolaNome).length > 0;
	},

	/**
	 * HTML escapado do nome com os trechos da escola destacados.
	 * Devolve '' quando não há nada a destacar.
	 */
	destacar(nome, escolaNome) {
		const f = faixas(nome, escolaNome);
		if (!f.length) return '';
		let html = '';
		let pos = 0;
		f.forEach(([ ini, fim ]) => {
			html += escapeHtml(nome.slice(pos, ini));
			html += '<mark class="lw:bg-yellow-400 lw:text-gray-900 lw:rounded lw:px-0.5">' + escapeHtml(nome.slice(ini, fim)) + '</mark>';
			pos = fim;
		});
		html += escapeHtml(nome.slice(pos));
		return html;
	},

	/**
	 * Substitui os trechos destacados pelo nome da escola de destino.
	 */
	substituir(nome, escolaNome, novoNome) {
		const f = faixas(nome, escolaNome);
		if (!f.length) return nome;
		let out = '';
		let pos = 0;
		f.forEach(([ ini, fim ]) => {
			out += nome.slice(pos, ini) + novoNome;
			pos = fim;
		});
		out += nome.slice(pos);
		return out.replace(/\s{2,}/g, ' ').trim();
	},
};
